import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES, QUIZ_HINT_COST_POINTS } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaOrange.png";
import "../StartGame.css";
import "../Scenario2/Scenario2.css";

/** Clases extra del popup SweetAlert2 (tema burdeos Cobega). */
const SC4_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--fepol";
/** Pista: borde cian, avatar enmarcado, botón confirmar en teal (ver modals.css · modal-sg5--hint-sc2). */
const SC4_HINT_THEME_CLASS = `${SC4_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();

const SC4_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM =
	"De la siguiente lista, selecciona las familias que entendieron que solo uniendo sus fuerzas podrían construir una unidad familiar más fuerte: una alianza capaz de dar el salto a Europa, reunir los recursos necesarios y ser un actor relevante en un escenario mucho más grande.";

const RETROALIMENTACION =
	"La respuesta correcta es: Familia Daurella, Familia Gómez Trenor, Famliia Comenge, Familia Masaveu Mora-Figueroa y Familia Usó Ferrera";

const SCENARIO4_QUIZ2_BASE_OPTIONS = [
	{
		id: 1,
		label: "A",
		text: "Familia Daurella",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 2,
		label: "B",
		text: "Familia Montcada-Beltrán",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 3,
		label: "C",
		text: "Familia Vidal-Quadras",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 4,
		label: "D",
		text: "Familia Gómez-Trenor",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 5,
		label: "E",
		text: "Familia Comenge",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 6,
		label: "F",
		text: "Familia Masaveu Mora-Figueroa",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 7,
		label: "G",
		text: "Familia Usó Ferrera",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 8,
		label: "H",
		text: "Familia Roca-Sagnier",
		correct: false,
		img: null,
		explanation: "",
	},
];

/** IDs de las opciones correctas (fijas; los `id` no cambian al barajar). */
const CORRECT_OPTION_IDS = new Set(
	SCENARIO4_QUIZ2_BASE_OPTIONS.filter((o) => o.correct).map((o) => o.id)
);

function selectionIsFullyCorrect(selectedIds) {
	if (selectedIds.size !== CORRECT_OPTION_IDS.size) return false;
	for (const id of CORRECT_OPTION_IDS) {
		if (!selectedIds.has(id)) return false;
	}
	return true;
}

function Scenario4Quiz2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ4;
	const SCENARIO_ID = SCENARIOS.SCENARIO_4;

	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -2;

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "orange");

	const displayOptions = useMemo(() => shuffleQuizOptions(SCENARIO4_QUIZ2_BASE_OPTIONS), []);

	const toggleOption = (id) => {
		if (isSubmitting || isCompleted) return;
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	async function submitAnswer(finalScore, correct) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO4_CONTEXT3,
				correct,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO4_CONTEXT3);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC4_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO4_CONTEXT3);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC4_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		const hint = `Da Gozo Construir Mayores Uniones.`;

		if (hintUsed) {
			await modalHintShow({
				hint,
				hintMarkHtml: SC4_HINT_MARK_HTML,
				color: "orange",
				big: false,
				themePopupClass: SC4_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			image: getRandomEstasseguroAvatar(),
			imageHintUsed,
			hint,
			hintMarkHtml: SC4_HINT_MARK_HTML,
			color: "orange",
			big: false,
			themePopupClass: SC4_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(QUIZ_HINT_COST_POINTS);
		}
	};

	function checkAnswer() {
		if (selectedIds.size === 0) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 orange">Marca al menos una opción o pulsa las que correspondan.</div>`,
				themePopupClass: SC4_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "orange",
			themePopupClass: SC4_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const fullCorrect = selectionIsFullyCorrect(selectedIds);

			if (fullCorrect) {
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = CORRECT_ANSWER_POINTS - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;

				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				const labelsMarked = displayOptions
					.filter((o) => selectedIds.has(o.id))
					.map((o) => `${o.label}. ${o.text}`)
					.join("<br/>");

				const capitalInicio = capitalAtQuizStartRef.current ?? 0;
				const netoEnEstaPregunta = newScore - capitalInicio;

				modalQuestionResolution({
					correct: true,
					question: "Todas las opciones correctas",
					textHtml: `${buildQuizResultAvatarHtml(getRandomAciertoAvatar(), "correct", "orange")}
                      <div class="txt22 bold pt-3 text-start">${labelsMarked}</div>
                      <div class="txt28 bold pt-4">Has ganado ${attemptDelta} pt en esta pregunta${hintThisAttempt ? ` (−${QUIZ_HINT_COST_POINTS} pt pista)` : ""}</div>
                      <div class="txt20 mt-2">Puntos en esta pregunta: <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt</div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "orange",
					themePopupClass: SC4_SWAL_POPUP_CLASS,
				}).then(() => {
					submitAnswer(newScore, true);
				});

				return;
			}

			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
			const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
			const newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			const capitalInicio = capitalAtQuizStartRef.current ?? 0;
			const netoEnEstaPregunta = newScore - capitalInicio;

			modalQuestionResolution({
				correct: false,
				question: "Selección incompleta o incorrecta",
				textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "orange")}
                    <div class="txt28 bold pt-3">Respuesta incorrecta: ${attemptDelta} pt ${hintThisAttempt ? `(incluye −${QUIZ_HINT_COST_POINTS} pt por pista)` : ""}</div>
                    <div class="txt20 mt-2">Puntos en esta pregunta: <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt</div>
                    ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "orange",
				themePopupClass: SC4_SWAL_POPUP_CLASS,
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand">
			<div className="start-game-inner start-game-inner--prep5">
				<span className="start-game-badge sg5-badge">
					<span className="start-game-badge-dot" aria-hidden />
					Escenario 3 · Coca-Cola
				</span>

				<div className="sg5-scroll">
					<header className="sg5-hero">
						<h1 className="sg5-title">Pregunta</h1>
						<p className="sg5-lead sc2-quiz-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>Marca todas las opciones correctas (puedes elegir varias) y pulsa Comprobar respuesta.</span>
						</p>
					</div>

					<div className="sc2-quiz-options">
						{displayOptions.map((option) => {
							const isSelected = selectedIds.has(option.id);
							return (
								<button
									key={option.id}
									type="button"
									className={`sg5-aside-card sc2-quiz-option text-start ${isSelected ? "sc2-quiz-option--selected" : ""}`}
									onClick={() => toggleOption(option.id)}
									aria-pressed={isSelected ? "true" : "false"}
									aria-label={`Opción ${option.label}: ${option.text}`}
								>
									<div className="sc2-quiz-option-row">
										<span className="sc2-quiz-option-letter">{option.label}</span>
										<span className="sc2-quiz-option-text">{option.text}</span>
									</div>
								</button>
							);
						})}
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={
								hintUsed
									? `Pista usada (−${QUIZ_HINT_COST_POINTS} pt)`
									: `Usar pista (−${QUIZ_HINT_COST_POINTS} pt)`
							}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? (
							<span className="badge-penalty sc2-quiz-hint-badge">−{QUIZ_HINT_COST_POINTS} pt</span>
						) : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default Scenario4Quiz2;
